import { Component, Input } from '@angular/core';
import { ValidationState } from '@model/codes-enums/enums';
import { PasswordPolicy } from '@model/password/password-policy';
import { PasswordRule, PasswordsMatchRule } from '@model/password/password-rule';

//
// Wraps a PasswordRule with some display helpers
export class PasswordRuleDisplay {
  private rule: PasswordRule;

  // The rule's description for presentation to users
  public get description(): string {
    return this.rule.description;
  }

  // The current ValidationState based on the last applied potential password
  public state: ValidationState;

  constructor(rule: PasswordRule) {
    //public set Rule(value: PasswordRule) {
    this.rule = rule;
    this.state = ValidationState.Neutral;
  }

  // Applies the rule to a given password, and updates our State
  public apply(password: string) {
    this.rule.apply(password)
      ? (this.state = ValidationState.Valid)
      : (this.state = ValidationState.Invalid);
  }
}

//
// Provides display for the password policy (and a local rule) to guide the user
@Component({
  selector: 'sp-password-policy',
  template: ` <ul class="fa-ul">
    <li class="cellvalidation" *ngFor="let rule of Rules">
      <span class="fa-li"
        ><i *ngIf="rule.state === 'Neutral'" class="fas fa-circle" aria-hidden="true"></i></span
      ><span class="fa-li"
        ><i *ngIf="rule.state === 'Invalid'" class="fa fa-times" aria-hidden="true"></i
        ><i *ngIf="rule.state === 'Valid'" class="fa fa-check" aria-hidden="true"></i
      ></span>
      <p>{{ rule.description }}</p>
    </li>
  </ul>`
})
export class PasswordPolicyComponent {
  public Rules: Array<PasswordRuleDisplay>;
  private password: string;

  // Supports "retype your password"
  private matchRule: PasswordsMatchRule;
  private matchRuleDisplay: PasswordRuleDisplay;

  // Supports "Rejected by server so we assume it's re-used"
  private noReuseRule: PasswordsMatchRule;
  private noReuseDisplay: PasswordRuleDisplay;

  constructor() {
    this.matchRule = new PasswordsMatchRule();
    this.matchRuleDisplay = new PasswordRuleDisplay(this.matchRule);

    this.noReuseRule = new PasswordsMatchRule();
    this.noReuseRule.isPositiveRule = false;
    //this is replacing "Can't use your last 8 passwords" for now
    this.noReuseRule.description = 'Please try another password.';
    this.noReuseDisplay = new PasswordRuleDisplay(this.noReuseRule);

    //"no reuse" gets added in only if needed
    this.Rules = [this.matchRuleDisplay];
  }

  // The password policy being presented
  private policy: PasswordPolicy;
  @Input()
  public get Policy(): PasswordPolicy {
    return this.policy;
  }
  public set Policy(value: PasswordPolicy) {
    this.policy = value;
    this.Rules = [this.matchRuleDisplay];
    if (value) {
      value.rules.forEach((rule) => this.Rules.push(new PasswordRuleDisplay(rule)));
    }
  }

  // The "primary" input from the user against which the policy will be evaluated
  @Input()
  public set Password(value: string) {
    this.password = value;

    if (value) {
      this.Rules.forEach((rule) => rule.apply(value));
    } else {
      this.Rules.forEach((rule) => (rule.state = ValidationState.Neutral));
    }
  }

  // The "confirm your password" input, this will update the "matching" rule and evaluate
  // the last provided Password.
  @Input()
  public set ConfirmPassword(value: string) {
    this.matchRule.toMatch = value;
    if (this.password) {
      this.Rules.forEach((rule) => rule.apply(this.password));
    }
  }

  private isOldPassword: boolean;
  // When set to true, the most-recently evaluated password is captured and we add the rule for
  // display.  When set false, the match target is cleared and the rule removed as per the old
  // message's behavior
  @Input()
  public set IsOldPassword(value: boolean) {
    if (value) {
      this.noReuseRule.toMatch = this.password;
      this.Rules.push(this.noReuseDisplay);
    } else {
      this.noReuseRule.toMatch = '';
      if (this.Rules.indexOf(this.noReuseDisplay)>=0)
      {
        this.Rules.splice(this.Rules.indexOf(this.noReuseDisplay), 1);
      }
    }
  }

  // Returns "true" if the most recently-checked password satisfied all rules.
  public get IsValid(): boolean {
    return this.Rules.every((r) => r.state === ValidationState.Valid);
  }
}
